import { useEffect, useLayoutEffect, useMemo, useRef } from 'react'

import { FocusRoot } from '@please/lrud'
import { BrowserRouter } from 'react-router-dom'
import { ToastContainer } from 'react-toastify'
import { ThemeProvider } from 'styled-components'

import { getActiveRegionId } from '@sportworld/js-sdk/src/actions/region'
import { useAuth } from '@sportworld/js-sdk/src/context/authContext'
import { isDevMode } from '@sportworld/js-sdk/src/envs/envs'
import { getUrlParam } from '@sportworld/js-sdk/src/utils'

import {
  updateAllChannels,
  updateAppSettings,
  getRegisteredUserId,
  initGTAG,
  initFavorites,
  updateAppConfig,
} from '@/actions/actions'
import { updateChannelEvents } from '@/actions/channelEvents'
import appLogo from '@/assets/images/logo_sportworld_white.svg'
import { AuthPopup } from '@/components/AuthPopup/AuthPopup'
import { BackNav } from '@/components/BackNav/BackNav'
import { ConfirmationPopup } from '@/components/ConfirmationPopup/ConfirmationPopup'
import { DialogPopUp } from '@/components/DialogPopUp'
import { InspectPane } from '@/components/InspectPane/'
import { PaymentPopup } from '@/components/PaymentPopup/PaymentPopup'
import { PerformanceMonitor } from '@/components/PerformanceMonitor/PerformanceMonitor'
import { PPVPopup } from '@/components/PPVPopup/PPVPopup'
import { Preloader, LogoPreloader } from '@/components/VideoPlayer/styles'
import { eventCategory } from '@/constants/analytics'
import { consentLoadedStatus, fetchStatus } from '@/constants/common'
import { goldTextPalette } from '@/constants/themes'
import { EventDetailsPopup } from '@/containers/EventDetailPage/EventDetailsPopup'
import { ListPopup } from '@/containers/ListPopup'
import { MediaPlayerPopup } from '@/containers/MediaPlayer/MediaPlayerPopup'
import { PagePopup } from '@/containers/Page/PagePopup'
import { UsercentricsPopup } from '@/containers/Usercentrics/Usercentrics'
import { loadUsercentrics } from '@/containers/Usercentrics/usercentricsUtils'
import { YoutubePlayerPopup } from '@/containers/YouTube/YoutubePlayerPopup'
import { useClearLegacyKeysFromLS } from '@/hooks/useClearLegacyKeysFromLS'
import { useCreditWalletsUpdate } from '@/hooks/useCreditWalletsUpdate'
import { PointerScroll } from '@/hooks/useCustomFocus'
import { PageRefresherProvider } from '@/hooks/usePageRefresher'
import { useSetPalette } from '@/hooks/useSetPalette'
import { useTizenKeys } from '@/hooks/useTizenKeys'
import { getDuid } from '@/platform/platform'
import { Routes } from '@/Routes'
import useStore from '@/store/useStore'
import { GlobalStyles } from '@/styles'
import {
  createTheme,
  sendEventToGTAG,
  isPointerMode,
  showDevModePane,
} from '@/utils/utils'

import 'react-toastify/dist/ReactToastify.css'

function App() {
  const { isAnonymousUser, isRegisteredUser, isAuthenticatedUserType } =
    useAuth()
  const appPalette = useStore((state) => state.common.appConfig.palette)
  const channelsLimit = useStore(
    (state) => state.common.appConfig.channelsLimit,
  )
  const appConfig = useStore((state) => state.common.appConfig)
  const appConfigStatus = useStore((state) => state.common.appConfigStatus)
  const appSettings = useStore((state) => state.common.appSettings)
  const customPalette = useStore((state) => state.common.customPalette)
  const consentStatus = useStore((state) => state.common.consentStatus)
  const setConsentStatus = useStore((state) => state.common.setConsentStatus)
  const isConsentCompleted = consentStatus === consentLoadedStatus.COMPLETED
  const appConfigCompleted = appConfigStatus === fetchStatus.COMPLETED

  useCreditWalletsUpdate()
  const { isPerformanceMonitorUsed, isCustomPaletteUsed } = appSettings
  const { updateAppPalette } = useSetPalette()
  const theme = useMemo(
    () => customPalette && createTheme(customPalette),
    [customPalette],
  )

  useEffect(() => {
    updateAppSettings()
    initGTAG()
  }, [])

  useLayoutEffect(() => {
    const palette =
      isCustomPaletteUsed && isDevMode ? goldTextPalette : appPalette
    updateAppPalette({ palette })
  }, [appPalette, isCustomPaletteUsed, isDevMode])

  const consentTimeout = useRef<NodeJS.Timeout | undefined>()

  useEffect(() => {
    if (consentStatus === consentLoadedStatus.LOADED) {
      consentTimeout.current = setTimeout(() => {
        setConsentStatus(consentLoadedStatus.COMPLETED)
      }, 5000)
    }
    return () => {
      consentTimeout.current && clearTimeout(consentTimeout.current)
    }
  }, [consentStatus])

  useEffect(() => {
    if (appConfigCompleted) {
      // GDPR → yg66BZnHi4yK4R
      // TCF → wh8xJjWMBiumuV
      // CCPA → wwnSwBbU2U9siO
      const params = new URLSearchParams(window.location.search)
      const ucpid = params.get('ucpid')
      const userCentricsId = ucpid || appConfig?.region?.userCentricsId
      if (userCentricsId) {
        loadUsercentrics(userCentricsId)
      } else {
        setConsentStatus(consentLoadedStatus.COMPLETED)
      }
    }
  }, [appConfigCompleted])

  useEffect(() => {
    if (appConfigStatus) {
      updateAllChannels(channelsLimit)
    }
  }, [appConfigStatus, channelsLimit, isAuthenticatedUserType])

  useEffect(() => {
    if (isAuthenticatedUserType) {
      updateChannelEvents(true, [])
    }
  }, [isAuthenticatedUserType])

  useEffect(() => {
    if (isRegisteredUser) {
      initFavorites()
    }
  }, [isRegisteredUser])

  const isAuthenticatedUser = useRef(false)

  useEffect(() => {
    let regionId = getUrlParam('regionId') || ''
    if (isDevMode) {
      const activeRegionId = getActiveRegionId()
      if (activeRegionId !== 'default') {
        regionId = activeRegionId
      }
    }
    if (isAuthenticatedUserType && !isAuthenticatedUser.current) {
      void updateAppConfig(regionId)
      isAuthenticatedUser.current = true
      return
    }
    isAnonymousUser && updateAppConfig(regionId)
  }, [isAuthenticatedUserType])

  useEffect(() => {
    if (!isAuthenticatedUserType) {
      return
    }

    const duid = getDuid()
    const userData = { type: isAuthenticatedUserType, duid }

    if (isAnonymousUser) {
      sendEventToGTAG(eventCategory.USER, userData)
      sendEventToGTAG(eventCategory.ANONYMOUS_USER, { duid })
    }
    if (isRegisteredUser) {
      const plenigo_uid = getRegisteredUserId()
      sendEventToGTAG(eventCategory.USER, { ...userData, plenigo_uid })
      sendEventToGTAG(eventCategory.REGISTERED_USER, { duid, plenigo_uid })
    }
  }, [isAuthenticatedUserType])

  useClearLegacyKeysFromLS()
  useTizenKeys()

  return (
    <ThemeProvider theme={theme}>
      <GlobalStyles />
      <div className="App">{renderApp()}</div>
    </ThemeProvider>
  )

  function renderApp() {
    return (
      <>
        {isConsentCompleted && appConfigCompleted && (
          <FocusRoot pointerEvents={isPointerMode}>
            <BrowserRouter>
              <PageRefresherProvider>
                <PointerScroll />
                <BackNav />
                <PagePopup />
                <EventDetailsPopup />
                <MediaPlayerPopup />
                <YoutubePlayerPopup />
                <Routes />
                <ListPopup />
                <PPVPopup />
                <AuthPopup />
                <PaymentPopup />
                <UsercentricsPopup />
              </PageRefresherProvider>
            </BrowserRouter>
            <DialogPopUp />
            <ConfirmationPopup />
          </FocusRoot>
        )}
        {(!appConfigStatus || !isConsentCompleted) && (
          <Preloader>
            <LogoPreloader $src={appLogo} $isBlocked={false}>
              <img src={appLogo} alt="Logo" />
            </LogoPreloader>
          </Preloader>
        )}
        <ToastContainer />
        {isDevMode && isPerformanceMonitorUsed && <PerformanceMonitor />}
        {showDevModePane && <InspectPane />}
      </>
    )
  }
}

export default App
